import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { ArrowLeft, CheckCheck, Send, ShoppingCart, Store, Package } from 'lucide-react';

const API_BASE_URL = 'https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api';

const PLATFORMS = {
  amazon: { color: '#2383e2', bg: 'rgba(35,131,226,0.06)', Icon: ShoppingCart },
  walmart: { color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', Icon: Store },
  shopify: { color: '#10b981', bg: 'rgba(16,185,129,0.06)', Icon: Package },
};

const TABLE_GRID =
  'minmax(220px, 2.2fr) repeat(3, minmax(42px, 0.52fr) minmax(42px, 0.52fr) minmax(42px, 0.52fr) minmax(64px, 0.72fr)) minmax(64px, 0.7fr) minmax(70px, 0.78fr)';

export default function ReceiveMasterPO() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:900px)');

  const [productDiffs, setProductDiffs] = useState([]);
  const [received, setReceived] = useState({});
  const [arrived, setArrived] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/master-pos/${id}/received`);
      const diffs = data.productDifferences || [];
      setProductDiffs(diffs);

      const r = {};
      const a = {};
      diffs.forEach((p) => {
        r[p.productId] = {
          amazon: p.platformBreakdown?.amazon || 0,
          walmart: p.platformBreakdown?.walmart || 0,
          shopify: p.platformBreakdown?.shopify || 0,
        };
        a[p.productId] = { amazon: 0, walmart: 0, shopify: 0 };
      });
      setReceived(r);
      setArrived(a);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to load PO', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const canReceiveAll = useMemo(() => {
    return productDiffs.some((p) => {
      const rec = received[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
      return ['amazon', 'walmart', 'shopify'].some(
        (pl) => (p.orderedBreakdown[pl] || 0) - rec[pl] > 0
      );
    });
  }, [productDiffs, received]);

  const canSubmit = useMemo(() => {
    return Object.values(arrived).some(({ shopify }) => shopify > 0);
  }, [arrived]);

  const handleArrivedChange = useCallback(
    (pid, platform, val) => {
      let v = Number(val.replace(/^0+/, '') || '0');
      const ordered = productDiffs.find((p) => p.productId === pid)?.orderedBreakdown[platform] || 0;
      const rec = received[pid]?.[platform] || 0;
      const rem = Math.max(0, ordered - rec);
      v = Math.max(0, Math.min(rem, v));

      setArrived((a) => ({
        ...a,
        [pid]: { ...a[pid], [platform]: v },
      }));
    },
    [productDiffs, received]
  );

  const handleReceiveAll = useCallback(() => {
    const newA = {};
    productDiffs.forEach((p) => {
      const rec = received[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
      newA[p.productId] = {
        amazon: Math.max(0, (p.orderedBreakdown.amazon || 0) - rec.amazon),
        walmart: Math.max(0, (p.orderedBreakdown.walmart || 0) - rec.walmart),
        shopify: Math.max(0, (p.orderedBreakdown.shopify || 0) - rec.shopify),
      };
    });
    setArrived(newA);
  }, [productDiffs, received]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setLoading(true);

    try {
      const receivedPayload = productDiffs.map((p) => {
        const rec = received[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
        const arr = arrived[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
        const breakdown = {
          amazon: rec.amazon + arr.amazon,
          walmart: rec.walmart + arr.walmart,
          shopify: rec.shopify + arr.shopify,
        };
        return {
          productId: p.productId,
          totalReceivedQuantity: breakdown.amazon + breakdown.walmart + breakdown.shopify,
          platformBreakdown: breakdown,
        };
      });

      await axios.put(`${API_BASE_URL}/master-pos/${id}/receive`, {
        receivedProducts: receivedPayload,
      });

      const arrivedProducts = Object.entries(arrived)
        .map(([productId, { shopify }]) => ({ productId, arrivedShopify: shopify }))
        .filter((item) => item.arrivedShopify > 0);

      const { data } = await axios.put(`${API_BASE_URL}/master-pos/${id}/submit-shopify`, {
        arrivedProducts,
      });

      setSnackbar({
        open: true,
        message: data.message || 'Update successful!',
        severity: 'success',
      });

      setArrived((a) => {
        const reset = {};
        Object.keys(a).forEach((pid) => {
          reset[pid] = { ...a[pid], shopify: 0 };
        });
        return reset;
      });

      await fetchData();
    } catch (err) {
      console.error('Submit error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      const userMsg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.message ||
        'Submit failed';

      setSnackbar({ open: true, message: userMsg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [arrived, canSubmit, id, fetchData, productDiffs, received]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} thickness={3} />
      </Box>
    );
  }

  return (
    <Box className="fade-in" sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowLeft size={16} />}
          sx={{ color: 'text.secondary', fontWeight: 500, alignSelf: 'flex-start' }}
        >
          Back
        </Button>

        <Button
          variant="outlined"
          startIcon={<CheckCheck size={16} />}
          onClick={handleReceiveAll}
          disabled={!canReceiveAll}
        >
          Receive All
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.75 }}>
          Receive Order
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Review ordered, received, and newly arrived quantities before submitting Shopify updates.
        </Typography>
      </Box>

      {/* Mobile: Card layout */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {productDiffs.length === 0 ? (
            <Box sx={{ px: 3, py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No products found for this order.</Typography>
            </Box>
          ) : productDiffs.map((p) => {
            const rec = received[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
            const arrv = arrived[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
            const totalOrd = ['amazon', 'walmart', 'shopify'].reduce((s, k) => s + (p.orderedBreakdown[k] || 0), 0);
            const totalRcv = rec.amazon + rec.walmart + rec.shopify;

            return (
              <Box key={p.productId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{p.productTitle}</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Ord: {totalOrd}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: totalOrd > totalRcv ? '#ef4444' : '#10b981' }}>Rcvd: {totalRcv}</Typography>
                  </Box>
                </Box>
                {Object.entries(PLATFORMS).map(([plat, { color, bg, Icon }]) => {
                  const ord = p.orderedBreakdown[plat] || 0;
                  const rcv = rec[plat] || 0;
                  const rem = ord - rcv;
                  if (ord === 0 && rcv === 0) return null;
                  return (
                    <Box key={plat} sx={{ px: 2, py: 1.25, bgcolor: bg, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
                        <Icon size={13} style={{ color }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'capitalize', fontSize: '0.72rem' }}>{plat}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, flex: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Ord: {ord}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Rcvd: {rcv}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: rem > 0 ? '#b45309' : 'text.primary' }}>Rem: {rem}</Typography>
                      </Box>
                      <TextField
                        type="number"
                        inputProps={{ min: 0, max: rem }}
                        value={arrv[plat]}
                        onChange={(e) => handleArrivedChange(p.productId, plat, e.target.value)}
                        disabled={rem === 0}
                        size="small"
                        placeholder="Arr"
                        sx={{
                          width: 64,
                          '& .MuiInputBase-root': { height: 30, bgcolor: 'background.paper' },
                          '& .MuiInputBase-input': { textAlign: 'center', fontSize: '0.74rem', py: 0.4, px: 0.5 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      ) : (
        /* Desktop: Grid table */
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: TABLE_GRID, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.64rem', color: 'text.secondary' }}>Product</Typography>
                </Box>
                {Object.entries(PLATFORMS).map(([key, { color, bg, Icon }]) => (
                  <Box key={key} sx={{ gridColumn: 'span 4', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 0.75, py: 1.5, bgcolor: bg, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Icon size={13} style={{ color }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.66rem', color, textTransform: 'capitalize', letterSpacing: '0.02em' }}>{key}</Typography>
                  </Box>
                ))}
                <Box sx={{ px: 0.75, py: 1.5, textAlign: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', color: 'text.secondary', letterSpacing: '0.04em' }}>Tot Ord</Typography>
                </Box>
                <Box sx={{ px: 0.75, py: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', color: 'text.secondary', letterSpacing: '0.04em' }}>Tot Rcvd</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: TABLE_GRID, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                <Box sx={{ borderRight: '1px solid', borderColor: 'divider' }} />
                {Object.entries(PLATFORMS).map(([key, { bg }]) => (
                  <React.Fragment key={key}>
                    {['Ord', 'Rcvd', 'Rem', 'Arr'].map((sub, idx) => (
                      <Box key={`${key}-${sub}`} sx={{ px: 0.35, py: 0.8, textAlign: 'center', bgcolor: bg, borderRight: idx === 3 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.58rem', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{sub}</Typography>
                      </Box>
                    ))}
                  </React.Fragment>
                ))}
                <Box sx={{ borderRight: '1px solid', borderColor: 'divider' }} />
                <Box />
              </Box>

              {productDiffs.map((p, rowIndex) => {
                const rec = received[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
                const arrv = arrived[p.productId] || { amazon: 0, walmart: 0, shopify: 0 };
                const totalOrd = ['amazon', 'walmart', 'shopify'].reduce((s, k) => s + (p.orderedBreakdown[k] || 0), 0);
                const totalRcv = rec.amazon + rec.walmart + rec.shopify;

                return (
                  <Box key={p.productId} sx={{
                    display: 'grid', gridTemplateColumns: TABLE_GRID, alignItems: 'center',
                    borderBottom: rowIndex === productDiffs.length - 1 ? 'none' : '1px solid',
                    borderColor: 'divider', bgcolor: rowIndex % 2 === 0 ? 'background.paper' : 'rgba(0,0,0,0.01)',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <Box sx={{ px: 2, py: 1.2, borderRight: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}>{p.productTitle}</Typography>
                    </Box>
                    {Object.entries(PLATFORMS).map(([plat, { bg }]) => {
                      const ord = p.orderedBreakdown[plat] || 0;
                      const rcv = rec[plat] || 0;
                      const rem = ord - rcv;
                      return (
                        <React.Fragment key={plat}>
                          <Box sx={{ textAlign: 'center', py: 1, px: 0.25, bgcolor: bg }}>
                            <Typography variant="body2" sx={{ fontSize: '0.76rem' }}>{ord}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', py: 1, px: 0.25, bgcolor: bg }}>
                            <Typography variant="body2" sx={{ fontSize: '0.76rem' }}>{rcv}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', py: 1, px: 0.25, bgcolor: bg }}>
                            <Typography variant="body2" sx={{ fontSize: '0.76rem', fontWeight: 600, color: rem > 0 ? '#b45309' : rem < 0 ? '#ef4444' : 'text.primary' }}>{rem}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', py: 0.7, px: 0.5, bgcolor: bg }}>
                            <TextField
                              type="number" inputProps={{ min: 0, max: rem }}
                              value={arrv[plat]} onChange={(e) => handleArrivedChange(p.productId, plat, e.target.value)}
                              disabled={rem === 0} size="small"
                              sx={{
                                width: '100%', maxWidth: 58,
                                '& .MuiInputBase-root': { height: 30, bgcolor: 'background.paper' },
                                '& .MuiInputBase-input': { textAlign: 'center', fontSize: '0.74rem', py: 0.4, px: 0.5 },
                              }}
                            />
                          </Box>
                        </React.Fragment>
                      );
                    })}
                    <Box sx={{ textAlign: 'center', py: 1, px: 0.5, borderLeft: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.76rem' }}>{totalOrd}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', py: 1, px: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.76rem', color: totalOrd > totalRcv ? '#ef4444' : '#10b981' }}>{totalRcv}</Typography>
                    </Box>
                  </Box>
                );
              })}

              {productDiffs.length === 0 && (
                <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>No products found for this order.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="contained"
          startIcon={<Send size={16} />}
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, px: 2.5 }}
        >
          Submit to Shopify
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}